import { baseDe, materialDaCamada, materialDoItem, semMaterialNaCamada, type Material } from '../look/materiais';
import { ARTE_DA_PECA, CAPA, MANTOS_COM_CAPUZ, ORDEM_CABECA, ORDEM_DO_TRAJE } from '../look/traje';
import { MONTARIA_PADRAO, MONTARIAS } from './montarias';
import { ehCamadaDeCajado } from '../data/armas';

/**
 * Folhas de personagem e animal: um JSON + um PNG, recortados uma vez.
 * A pé: um blit. Montado: a montaria (`animal/<id>`) depois o corpo no lombo, na pose `horse`.
 */

export const SKIN_PADRAO = 'ghost';

/** Corpo caído: a folha `character/human-male/die`, nunca a pele ghost. */
export const PELE_CADAVER = 'human-male';

const ESTILO_OK = /^[a-z0-9-]+$/;

/** `ajuste` no JSON da folha, somado à âncora no load — zero custo por quadro. */
export function aplicarAjuste(
  ancoraX: number,
  ancoraY: number,
  ajuste: unknown
): { ancoraX: number; ancoraY: number } {
  const dx = Array.isArray(ajuste) ? Number(ajuste[0]) : 0;
  const dy = Array.isArray(ajuste) ? Number(ajuste[1]) : 0;
  return {
    ancoraX: ancoraX - (Number.isFinite(dx) ? dx : 0),
    ancoraY: ancoraY - (Number.isFinite(dy) ? dy : 0)
  };
}

export function estiloDaFolha(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  return ESTILO_OK.test(s) && s !== 'none' ? s : '';
}

/**
 * Um quadro é um RECORTE da folha, não uma cópia dele. `flip` diz que sai
 * espelhado; quem desenha resolve isso com uma transformada.
 *
 * Já foi cópia: o `recortar` antigo criava um canvas por (direção × quadro), o
 * que duplicava os pixels da folha 8/5 vezes só para pré-assar as 3 direções
 * espelhadas. Nas células antigas, cada uma apertada na sua arte, isso passava
 * batido; com a célula-padrão do projeto virou o dobro do que a folha ocupa —
 * e o `pele.ts`, que tinge quadro por quadro, pagava uma cópia inteira por tom
 * de pele, guardada para sempre.
 * Recorte é de graça: `drawImage` aceita retângulo de origem, e o espelho é
 * um `scale(-1,1)`. Ver desenharQuadro.
 */
export interface Quadro {
  readonly fonte: CanvasImageSource;
  readonly sx: number;
  readonly sy: number;
  readonly w: number;
  readonly h: number;
  readonly flip: boolean;
}

export interface Folha {
  readonly url: string;
  readonly cellW: number;
  readonly cellH: number;
  readonly frames: number;
  readonly fps: number;
  readonly ancoraX: number;
  readonly ancoraY: number;
  /** A folha inteira, uma imagem só. O `pele.ts` troca por uma versão tingida. */
  readonly fonte: CanvasImageSource;
  /** [dir 0..7][quadro] — recortes, com `flip` nas 3 direções espelhadas. */
  readonly dirs: readonly (readonly Quadro[])[];
}

type DirSlice = { row: number; flip: boolean };

interface Meta {
  cellW: number;
  cellH: number;
  frames: number;
  fps: number;
  ancoraX: number;
  ancoraY: number;
  image: string;
  dirs: DirSlice[];
}

const cache = new Map<string, Promise<Folha>>();

function parseMeta(raw: Record<string, unknown>, jsonUrl: string): Meta {
  const base = jsonUrl.replace(/[^/]+$/, '');
  const ancora = (x: number, y: number) => aplicarAjuste(x, y, raw.ajuste);
  if (Array.isArray(raw.celula) && Array.isArray(raw.direcoes)) {
    const cell = raw.celula as number[];
    const dirs = (raw.direcoes as Array<{ dir: number; linha: number; espelhar?: boolean }>)
      .slice()
      .sort((a, b) => a.dir - b.dir)
      .map(d => ({ row: d.linha, flip: !!d.espelhar }));
    const a = ancora(Number(raw.ancoraX), Number(raw.ancoraY));
    return {
      cellW: Number(cell[0]),
      cellH: Number(cell[1]),
      frames: Number(raw.quadros),
      fps: Number(raw.fps) || 8,
      ancoraX: a.ancoraX,
      ancoraY: a.ancoraY,
      image: base + String(raw.imagem),
      dirs
    };
  }
  if (Array.isArray(raw.celula) && (Array.isArray(raw.variantes) || Number(raw.linhas) > 0)) {
    const cell = raw.celula as number[];
    const nLinhas = Math.max(1, Number(raw.linhas) || (raw.variantes as unknown[]).length);
    const a = ancora(Number(raw.ancoraX), Number(raw.ancoraY));
    return {
      cellW: Number(cell[0]),
      cellH: Number(cell[1]),
      frames: Number(raw.quadros),
      fps: Number(raw.fps) || 8,
      ancoraX: a.ancoraX,
      ancoraY: a.ancoraY,
      image: base + String(raw.imagem),
      dirs: Array.from({ length: nLinhas }, (_, i) => ({ row: i, flip: false }))
    };
  }
  // O formato antigo (cell/anchorX, do gabarito walk.png) saiu com o gabarito (techspec-loading).
  throw new Error(`folha em formato desconhecido: ${jsonUrl}`);
}

function fatiar(fonte: CanvasImageSource, meta: Meta): Quadro[][] {
  return meta.dirs.map(spec =>
    Array.from({ length: meta.frames }, (_, fr) => ({
      fonte,
      sx: fr * meta.cellW,
      sy: spec.row * meta.cellH,
      w: meta.cellW,
      h: meta.cellH,
      flip: spec.flip
    }))
  );
}

/**
 * Desenha um quadro em (dx,dy) com tamanho (dw,dh). Espelha em torno do MEIO do
 * retângulo de destino — que é exatamente onde a âncora cai, porque a
 * célula-padrão tem `ancoraX = celula/2`. É a mesma conta do espelho da célula,
 * sem a cópia dos pixels.
 */
export function desenharQuadro(
  ctx: CanvasRenderingContext2D,
  q: Quadro,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): void {
  if (!q.flip) {
    ctx.drawImage(q.fonte, q.sx, q.sy, q.w, q.h, dx, dy, dw, dh);
    return;
  }
  ctx.save();
  ctx.translate(dx + dw, dy);
  ctx.scale(-1, 1);
  ctx.drawImage(q.fonte, q.sx, q.sy, q.w, q.h, 0, 0, dw, dh);
  ctx.restore();
}

function carregarImagem(url: string): Promise<HTMLImageElement> {
  return new Promise((ok, err) => {
    const img = new Image();
    img.onload = () => ok(img);
    img.onerror = () => err(new Error(`Não deu para abrir a folha: ${url}`));
    img.src = url;
  });
}

/** Personagem a pé ou montado. `acao`: idle, walking, attack-…. */
export function jsonPersonagem(body: string, mount: string, acao: string): string {
  if (mount === 'horse') {
    return `/sprites/character/${body}/horse/${acao}/${body}-horse-${acao}.json`;
  }
  return `/sprites/character/${body}/${acao}/${body}-${acao}.json`;
}

/**
 * Os materiais de armadura com folha pronta. A pasta pública é
 * `character/<raiz>/<peça>` — `plates` é plural por acidente histórico, os
 * outros usam o próprio nome. `pecas` é a lista do que **existe**; pedir por
 * outra devolve `null` em vez de um 404. O `studded/helm` é o leather cap da UO
 * (anim 560, techspec-elmo-de-couro).
 */
const MATERIAIS: Readonly<Record<string, { raiz: string; pecas: readonly string[] }>> = {
  plate: { raiz: 'plates', pecas: ['armor', 'arms', 'gloves', 'helm', 'legs'] },
  studded: { raiz: 'studded', pecas: ['armor', 'arms', 'gloves', 'helm', 'legs'] },
  bone: { raiz: 'bone', pecas: ['armor', 'arms', 'gloves', 'helm', 'legs'] }
};

/** `plate` → `plates/armor`; `bone-helm` → `bone/helm`; `robe` → `cloth/robe`. */
function caminhoDaCamada(kind: string): { raiz: string; pasta: string } | null {
  if (kind === 'robe') return { raiz: 'cloth', pasta: 'robe' };
  const corte = kind.indexOf('-');
  const material = corte < 0 ? kind : kind.slice(0, corte);
  const pasta = corte < 0 ? 'armor' : kind.slice(corte + 1);
  const spec = MATERIAIS[material];
  if (!spec || !spec.pecas.includes(pasta)) return null;
  return { raiz: spec.raiz, pasta };
}

/** Camada de roupa compartilhada entre skins. */
export function jsonCloth(cloth: string, mount: string, acao: string): string {
  const traje = trajeDaCamada(cloth);
  if (traje) {
    const peca = ARTE_DA_PECA[traje.peca] ?? traje.peca;
    if (mount === 'horse') return `/sprites/character/traje/${peca}/horse/${acao}/${peca}-horse-${acao}.json`;
    return `/sprites/character/traje/${peca}/${acao}/${peca}-${acao}.json`;
  }
  const spec = caminhoDaCamada(pastaDaCloth(cloth));
  if (!spec) return '';
  const { raiz, pasta } = spec;
  if (mount === 'horse') {
    return `/sprites/character/${raiz}/${pasta}/horse/${acao}/${pasta}-horse-${acao}.json`;
  }
  return `/sprites/character/${raiz}/${pasta}/${acao}/${pasta}-${acao}.json`;
}

function jsonCamadaEstilo(raiz: 'hair' | 'beard', estilo: string, mount: string, acao: string): string {
  const id = estiloDaFolha(estilo);
  if (!id) return '';
  const nome = `${raiz}-${id}`;
  if (mount === 'horse') {
    return `/sprites/character/${raiz}/${id}/horse/${acao}/${nome}-horse-${acao}.json`;
  }
  return `/sprites/character/${raiz}/${id}/${acao}/${nome}-${acao}.json`;
}

export function jsonCabelo(estilo: string, mount: string, acao: string): string {
  return jsonCamadaEstilo('hair', estilo, mount, acao);
}

export function jsonBarba(estilo: string, mount: string, acao: string): string {
  return jsonCamadaEstilo('beard', estilo, mount, acao);
}

export function pastaDoElmo(defId: string | null | undefined): string {
  return ehCamadaElmo(defId) ? (defId ?? '').trim() : '';
}

/** Qualquer elmo, de qualquer material — não só o de placas. */
export function ehCamadaElmo(defId: string | null | undefined): boolean {
  return pecaDaCamada(pastaDaCloth(defId)) === 'helm';
}

/** Pernas → peito → braços → luvas → elmo → robe. */
const SLOTS_CLOTH = ['legs', 'chest', 'arms', 'gloves', 'helm', 'cloth'] as const;
/**
 * Ordem de pintura, **por peça** e não por material: com três materiais, uma
 * lista chapada de ids teria 14 linhas e um esquecimento pintaria a perna por
 * cima do peito. `armor` é o peito. As peças do guarda-roupa entram na mesma
 * escala, na ordem do UO (look/traje.ts): camisa e calça embaixo da placa, o
 * gibão por cima dela, o manto e a capa por cima de tudo, o chapéu por último.
 */
const ORDEM_PECAS: Readonly<Record<string, number>> = {
  legs: 50, armor: 60, arms: 70, gloves: 90, robe: 110, helm: ORDEM_CABECA
};

/**
 * Camada do guarda-roupa no fio: `t:<peça>:<matiz>` (techspec-guardaroupa). Peça desconhecida devolve null e
 * a camada não é pintada — cliente velho e peça nova não quebram nada.
 */
export function trajeDaCamada(part: string | null | undefined): { peca: string; hue: number } | null {
  const raw = (part ?? '').trim();
  if (!raw.startsWith('t:')) return null;
  const [, peca, h] = raw.split(':');
  if (!peca || !Object.prototype.hasOwnProperty.call(ORDEM_DO_TRAJE, peca)) return null;
  const hue = Number(h);
  return { peca, hue: Number.isInteger(hue) && hue > 0 && hue < 0x4000 ? hue : 0 };
}

/** A posição da camada na ordem de pintura. Desconhecida vai para o fim. */
export function ordemDaCamada(kind: string): number {
  const traje = trajeDaCamada(kind);
  if (traje) return ORDEM_DO_TRAJE[traje.peca];
  return ORDEM_PECAS[pecaDaCamada(kind)] ?? 999;
}

/** Elmo ou chapéu: pintam depois do cabelo. */
export function ehCamadaCabeca(kind: string | null | undefined): boolean {
  return !!kind && ordemDaCamada(pastaDaCloth(kind)) === ORDEM_CABECA;
}

/** O fio tem um manto com capuz (techspec-skins §03): o motor não pinta cabelo, chapéu nem elmo. */
export function capuzNoFio(cloth: string | null | undefined): boolean {
  return (cloth ?? '').split('+').some(p => MANTOS_COM_CAPUZ.has(trajeDaCamada(p)?.peca ?? ''));
}

/** A capa: atrás do corpo quando ele olha para a câmera (linhas 0 e 1 da folha), por cima no resto. */
export function ehCapa(kind: string | null | undefined): boolean {
  return trajeDaCamada(kind)?.peca === CAPA;
}

/** A linha da folha (0–4) de cada direção do motor — a mesma tabela do celula_padrao.py. */
const LINHA_DA_DIRECAO = [1, 0, 1, 2, 3, 4, 3, 2] as const;

export function capaAtras(dir: number): boolean {
  return (LINHA_DA_DIRECAO[((dir % 8) + 8) % 8] ?? 0) <= 1;
}

/** `bone-legs` → `legs`; `plate` → `armor`; `robe` → `robe`; nada → ''. */
function pecaDaCamada(kind: string): string {
  if (!kind) return '';
  if (kind.startsWith('t:')) return 'traje';
  if (kind === 'robe') return 'robe';
  const corte = kind.indexOf('-');
  return corte < 0 ? 'armor' : kind.slice(corte + 1);
}

/** `plate` + Valorite → `plate:valorite`. Sem material, ou no padrão (Iron), fica só a camada. */
function fioDaCamada(pasta: string, material: Material | null | undefined): string {
  return material && !material.padrao ? `${pasta}:${material.id}` : pasta;
}

/**
 * Id da camada sem o material. `plate:valorite`, `plate:8` (o fio antigo), `plate-chest` e
 * `plate-chest@valorite` caem no mesmo lugar.
 */
export function idDaCamada(defId: string | null | undefined): string {
  return baseDe(semMaterialNaCamada(defId));
}

/** O material da camada: o sufixo do fio (`plate:valorite`) ou o do item (`plate-chest@valorite`). */
export function materialDaPeca(defId: string | null | undefined): Material | null {
  const raw = (defId ?? '').trim();
  if (raw.startsWith('t:')) return null;
  if (raw.includes('@')) return materialDoItem(raw);
  return materialDaCamada(raw);
}

/**
 * Id da camada no fio. `plate-chest` é o **item**; `plate` é a **folha** — o
 * peito é o único que perde o sufixo, e é assim desde a primeira versão do fio.
 * Id sem folha (material desconhecido, ou peça que o material não tem)
 * devolve '' e a camada simplesmente não é pintada.
 */
export function pastaDaCloth(defId: string | null | undefined): string {
  const traje = trajeDaCamada(defId);
  if (traje) return `t:${traje.peca}:${traje.hue}`;
  const id = idDaCamada(defId);
  if (id === 'robe') return 'robe';
  const peito = id.endsWith('-chest') ? id.slice(0, -'-chest'.length) : id;
  return caminhoDaCamada(peito) ? peito : '';
}

/** Plate embaixo, robe em cima. `plate:valorite+robe` no fio — o `:x` é o metal. */
export function camadasDaCloth(defId: string | null | undefined): string[] {
  const raw = (defId ?? '').trim();
  if (!raw) return [];
  const seen = new Map<string, Material | null>();
  for (const part of raw.split('+')) {
    const pasta = pastaDaCloth(part);
    if (pasta) seen.set(pasta, materialDaPeca(part));
  }
  const ordenadas = [...seen.keys()].sort((a, b) => ordemDaCamada(a) - ordemDaCamada(b));
  return ordenadas.map(k => (trajeDaCamada(k) ? k : fioDaCamada(k, seen.get(k))));
}

/**
 * O fio que o motor guarda: as camadas que ele pinta, na ordem, e as skins de cajado no fim (techspec-skins §03).
 * Elas não são camada do corpo; ficam no fio para a maça na mão virar a skin (`armaComSkin`).
 */
export function fioDoVisual(defId: string | null | undefined): string {
  const cajados = (defId ?? '').split('+').map(p => p.trim()).filter(ehCamadaDeCajado);
  return [...camadasDaCloth(defId), ...new Set(cajados)].join('+');
}

/** Peças no paperdoll, robe por último. Devolve o fio ou vazio. */
export function overlayDoPack(
  items: readonly { equippedSlot?: string | null; defId: string }[]
): string {
  const layers: string[] = [];
  for (const slot of SLOTS_CLOTH) {
    const item = items.find(i => i.equippedSlot === slot);
    const pasta = pastaDaCloth(item?.defId);
    if (!pasta || !item) continue;
    layers.push(fioDaCamada(pasta, materialDoItem(item.defId)));
  }
  return layers.join('+');
}

/** Pasta em `character/weapons/`. Hoje só o heater tem folha de escudo (de qualquer metal). */
export function pastaDoEscudo(defId: string | null | undefined): string {
  return baseDe(defId) === 'heater' ? 'heater' : '';
}

/** Poses a pé do heater. O golpe da katana pede `attack-slash-1h`. */
const ESCUDO_PES = [
  'idle', 'walking', 'running',
  'attack-slash-1h', 'attack-slash-2h',
  'attack-pierce-1h', 'attack-pierce-2h',
  'attack-bash-1h', 'attack-bash-2h',
  'attack-bow', 'attack-crossbow', 'attack-wrestle',
  'spell', 'spell-area'
] as const;

const ESCUDO_CAVALO = [
  'idle', 'walking',
  'attack-slash', 'attack-bow', 'attack-crossbow'
] as const;

export function folhasDoEscudo(defId: string): string[] {
  const id = pastaDoEscudo(defId);
  if (!id) return [];
  return [
    ...ESCUDO_PES.map(pose => jsonArma(id, pose, '')),
    ...ESCUDO_CAVALO.map(pose => jsonArma(id, pose, 'horse'))
  ];
}

/** Overlay de arma em `character/weapons/<id>/`. Sem folha a pé, o motor cai na do cavalo. */
export function jsonArma(stem: string, acao: string, mount: string): string {
  const id = (stem ?? '').trim();
  const pose = (acao ?? '').trim();
  if (!id || !pose) return '';
  if (mount === 'horse') {
    return `/sprites/character/weapons/${id}/horse/${pose}/${id}-horse-${pose}.json`;
  }
  return `/sprites/character/weapons/${id}/${pose}/${id}-${pose}.json`;
}

export function folhasDaArma(stem: string): string[] {
  const id = (stem ?? '').trim();
  if (!id) return [];
  return [
    jsonArma(id, 'idle', ''),
    jsonArma(id, 'walking', ''),
    jsonArma(id, 'running', ''),
    jsonArma(id, 'attack', ''),
    jsonArma(id, 'idle', 'horse'),
    jsonArma(id, 'walking', 'horse'),
    jsonArma(id, 'attack', 'horse')
  ];
}

/**
 * Animal sozinho — estábulo, preview, montaria no mapa.
 *
 * `pasta` e `stem` andam separados porque cada cor é a MESMA arte do bicho
 * passada pela matiz da UO (tools/vd-para-montaria.py): a pasta é o id
 * (`cavalo-cinza-fogo`), o nome das folhas é o do bicho (`cavalo-cinza-idle`).
 * As 117 saem de `world/montarias.ts`.
 */
const ANIMAL_FOLHA: Readonly<Record<string, { pasta: string; stem: string }>> = Object.fromEntries(
  MONTARIAS.map(m => [m.id, { pasta: m.id, stem: m.bicho }])
);

/**
 * Só estas entram no prefetch. As outras chegam por `ensureAnimal()` quando
 * alguém aparece montado nelas: cada montaria custa ~1,7 MB de imagem
 * decodificada (112x128 x 5 linhas x 6 quadros; os dragões e o hiryu têm célula
 * maior), e baixar as 117 de saída custaria 200 MB por uma arte que quase
 * ninguém tem. Só o cavalo do estábulo vem antes.
 */
const MONTARIAS_QUENTES: readonly string[] = [MONTARIA_PADRAO];

export function ehMontaria(id: string | null | undefined): boolean {
  return !!id && Object.prototype.hasOwnProperty.call(ANIMAL_FOLHA, id);
}

export function jsonAnimal(kind: string, moving: boolean): string {
  const acao = moving ? 'walking' : 'idle';
  const spec = ANIMAL_FOLHA[kind] ?? ANIMAL_FOLHA[MONTARIA_PADRAO];
  return `/sprites/animal/${spec.pasta}/${acao}/${spec.stem}-${acao}.json`;
}

export function folhasDosAnimais(): string[] {
  return MONTARIAS_QUENTES.flatMap(id => [jsonAnimal(id, false), jsonAnimal(id, true)]);
}

/** Inimigo no mapa. idle | walking | attack | die. */
export function jsonInimigo(kind: string, moving: boolean, attack = false, die = false): string {
  const id = (kind ?? '').trim() || 'skeleton';
  const acao = die ? 'die' : attack ? 'attack' : moving ? 'walking' : 'idle';
  return `/sprites/enemies/${id}/${acao}/${id}-${acao}.json`;
}

export function folhasDoInimigo(kind: string): string[] {
  return [
    jsonInimigo(kind, false),
    jsonInimigo(kind, true),
    jsonInimigo(kind, false, true),
    jsonInimigo(kind, false, false, true)
  ];
}

const PE_ATAQUE = [
  'attack-slash-1h', 'attack-slash-2h',
  'attack-pierce-1h', 'attack-pierce-2h',
  'attack-bash-1h', 'attack-bash-2h',
  'attack-bow', 'attack-crossbow',
  // O soco (sem arma): corpo, cabelo, barba, placas e todas as peças do traje têm a folha (remontar-personagem.py).
  'attack-wrestle'
] as const;
const CAVALO_ATAQUE = ['attack-slash', 'attack-bow', 'attack-crossbow'] as const;

export function folhasDaSkin(body: string): string[] {
  const aPe = [
    jsonPersonagem(body, '', 'idle'),
    jsonPersonagem(body, '', 'walking')
  ];
  const cavalo = [
    jsonPersonagem(body, 'horse', 'idle'),
    jsonPersonagem(body, 'horse', 'walking')
  ];
  // Ghost só tem idle/walking. Pedir golpe 404 e disparava
  // `mundo.folhaFaltando` em todo EnterWorld.
  if (body === SKIN_PADRAO) return [...aPe, ...cavalo];
  return [
    ...aPe,
    jsonPersonagem(body, '', 'running'),
    ...PE_ATAQUE.map(a => jsonPersonagem(body, '', a)),
    // Conjurar (techspec-magias-animacoes): grupo 16 (mão à frente) e 17 (braços para o alto).
    ...POSES_MAGIA.map(a => jsonPersonagem(body, '', a)),
    ...cavalo,
    ...CAVALO_ATAQUE.map(a => jsonPersonagem(body, 'horse', a))
  ];
}

/** As poses de conjurar: a pé, em todas as camadas (corpo, placas, couro, osso, cabelo, barba, traje e skins). */
export const POSES_MAGIA = ['spell', 'spell-area'] as const;

const CLOTH_PES_PADRAO = ['idle', 'walking', 'running', ...PE_ATAQUE, ...POSES_MAGIA] as const;
const CLOTH_CAVALO_PADRAO = ['idle', 'walking', ...CAVALO_ATAQUE] as const;

/**
 * O robe só tem idle/walking/running a pé e idle/walking/attack-slash a cavalo.
 * Pedir golpe 404 e disparava `mundo.folhaFaltando` em todo EnterWorld.
 */
const CLOTH_ACOES: Readonly<Record<string, { pe: readonly string[]; cavalo: readonly string[] }>> = {
  robe: {
    pe: ['idle', 'walking', 'running'],
    cavalo: ['idle', 'walking', 'attack-slash']
  }
};

export function folhasDaCloth(cloth: string): string[] {
  const kind = pastaDaCloth(cloth);
  if (!kind) return [];
  const spec = CLOTH_ACOES[kind];
  const pe = spec?.pe ?? CLOTH_PES_PADRAO;
  const cavalo = spec?.cavalo ?? CLOTH_CAVALO_PADRAO;
  return [
    ...pe.map(a => jsonCloth(kind, '', a)),
    ...cavalo.map(a => jsonCloth(kind, 'horse', a))
  ].filter(Boolean);
}

function folhasDeEstilo(fn: (estilo: string, mount: string, acao: string) => string, estilo: string): string[] {
  if (!estiloDaFolha(estilo)) return [];
  return [
    fn(estilo, '', 'idle'),
    fn(estilo, '', 'walking'),
    fn(estilo, '', 'running'),
    ...PE_ATAQUE.map(a => fn(estilo, '', a)),
    ...POSES_MAGIA.map(a => fn(estilo, '', a)),
    fn(estilo, '', 'die'),
    fn(estilo, 'horse', 'idle'),
    fn(estilo, 'horse', 'walking'),
    ...CAVALO_ATAQUE.map(a => fn(estilo, 'horse', a))
  ];
}

export function folhasDoCabelo(estilo: string): string[] {
  return folhasDeEstilo(jsonCabelo, estilo);
}

export function folhasDaBarba(estilo: string): string[] {
  return folhasDeEstilo(jsonBarba, estilo);
}

/**
 * Marca curta do texto do JSON (FNV-1a 32 bits, base 36). Vai no `?v=` do PNG: quando a arte é refeita
 * (célula, âncora, quadros), o JSON muda e o PNG ganha outra URL. Sem isso, um navegador com o PNG antigo
 * no cache e o JSON novo (ou o contrário) fatiava a folha com a célula errada e o bicho saía em pedaços.
 */
export function marcaDoTexto(texto: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i++) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** JSON da folha: sempre revalida no servidor (304 se igual), mesmo com uma cópia antiga ainda "fresca" no cache. */
export async function lerJsonDaFolha(jsonUrl: string): Promise<{ raw: Record<string, unknown>; marca: string }> {
  const res = await fetch(jsonUrl, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Folha ${jsonUrl}: ${res.status}`);
  const texto = await res.text();
  return { raw: JSON.parse(texto) as Record<string, unknown>, marca: marcaDoTexto(texto) };
}

export function carregarFolha(jsonUrl: string): Promise<Folha> {
  if (!jsonUrl) return Promise.reject(new Error('Folha vazia'));
  const hit = cache.get(jsonUrl);
  if (hit) return hit;
  const job = (async () => {
    const { raw, marca } = await lerJsonDaFolha(jsonUrl);
    const meta = parseMeta(raw, jsonUrl);
    const img = await carregarImagem(`${meta.image}?v=${marca}`);
    return {
      url: jsonUrl,
      cellW: meta.cellW,
      cellH: meta.cellH,
      frames: meta.frames,
      fps: meta.fps,
      ancoraX: meta.ancoraX,
      ancoraY: meta.ancoraY,
      fonte: img,
      dirs: fatiar(img, meta)
    } satisfies Folha;
  })();
  cache.set(jsonUrl, job);
  return job;
}

const PREFETCH_ANIMAL = folhasDosAnimais();

const PREFETCH_INIMIGO = folhasDoInimigo('skeleton');

/** Dispara as folhas da skin e o cavalo. Falha isolada não derruba as outras. */
export function prefetchFolhas(skin?: string): Promise<void> {
  const body = (skin ?? '').trim() || SKIN_PADRAO;
  const urls = [
    ...folhasDaSkin(SKIN_PADRAO),
    ...(body !== SKIN_PADRAO ? folhasDaSkin(body) : []),
    ...(body !== SKIN_PADRAO ? folhasDaCloth('robe') : []),
    ...(body !== SKIN_PADRAO ? folhasDaCloth('studded') : []),
    ...(body !== SKIN_PADRAO ? folhasDaCloth('plate') : []),
    ...(body !== SKIN_PADRAO ? folhasDaCloth('plate-arms') : []),
    ...(body !== SKIN_PADRAO ? folhasDaCloth('plate-gloves') : []),
    ...(body !== SKIN_PADRAO ? folhasDaCloth('plate-helm') : []),
    ...(body !== SKIN_PADRAO ? folhasDaCloth('plate-legs') : []),
    ...(body !== SKIN_PADRAO ? folhasDaSkin('human-female') : []),
    ...(body !== SKIN_PADRAO ? folhasDoCabelo('ponytail') : []),
    ...(body !== SKIN_PADRAO ? folhasDaBarba('short') : []),
    ...PREFETCH_ANIMAL,
    ...PREFETCH_INIMIGO,
    ...folhasDaArma('katana'),
    ...folhasDoEscudo('heater'),
    jsonPersonagem(PELE_CADAVER, '', 'die')
  ];
  return Promise.allSettled(urls.map(url => carregarFolha(url))).then(() => undefined);
}

/** Quadros de walk por tile. Um ciclo inteiro por casa ficava acelerado demais. */
export const WALK_FRAMES_PER_TILE = 1.35;

export function quadroDa(folha: Folha, moving: boolean, gait: number, now: number, once = false): number {
  const n = Math.max(1, folha.frames);
  if (n <= 1) return 0;
  if (once) {
    const period = 1000 / Math.max(1, folha.fps);
    return Math.min(n - 1, Math.floor(Math.max(0, now) / period));
  }
  if (moving) return Math.floor(Math.max(0, gait) * WALK_FRAMES_PER_TILE) % n;
  const period = 1000 / Math.max(1, folha.fps);
  return Math.floor(Math.max(0, now) / period) % n;
}

/** Quadro de descanso no hold (idle / ghost): o 2, não o 0. */
export const IDLE_HOLD_QUADRO = 2;

/** Pose parada: um quadro só, o de descanso. */
export function folhaParada(folha: Folha, quadro = 0): Folha {
  const n = Math.max(1, folha.frames);
  const i = Math.max(0, Math.min(n - 1, quadro));
  if (n <= 1 && i === 0) return folha;
  return {
    ...folha,
    frames: 1,
    dirs: folha.dirs.map(row => {
      const cell = row[i] ?? row[0];
      return cell ? [cell] : [...row];
    })
  };
}

function folhaIdlePlay(folha: Folha): Folha {
  const fps = Math.max(1, folha.fps - 2);
  return fps === folha.fps ? folha : { ...folha, fps };
}

/** Pose parada por 2 s, depois um ciclo de idle mais lento, e recomeça. */
export const IDLE_HOLD_MS = 2000;

export function msDaFolha(folha: Folha): number {
  return (Math.max(1, folha.frames) / Math.max(1, folha.fps)) * 1000;
}

export interface IdlePick {
  readonly folha: Folha;
  readonly now: number;
  readonly once: boolean;
  /** Quadro congelado no hold. Overlay (robe) precisa do mesmo índice. */
  readonly holdQuadro?: number;
}

export function cicloIdle(
  hold: Folha | undefined,
  fidget: Folha | undefined,
  idleAt: number,
  now: number,
  fallback: Folha
): IdlePick {
  const pose = hold ?? fidget ?? fallback;
  const action = fidget ?? (hold && hold.frames > 1 ? hold : undefined);
  const restSrc = action && action.frames > IDLE_HOLD_QUADRO ? action : pose;
  const rest = restSrc.frames > 1 ? Math.min(IDLE_HOLD_QUADRO, restSrc.frames - 1) : 0;
  const frozen = (): IdlePick => ({
    folha: restSrc,
    now: restSrc.frames > 1 ? rest * (1000 / Math.max(1, restSrc.fps)) : 0,
    once: true,
    holdQuadro: rest
  });
  if (!action) return frozen();
  const play = folhaIdlePlay(action);
  const holdMs = IDLE_HOLD_MS;
  const fidgetMs = msDaFolha(play);
  const cycle = holdMs + fidgetMs;
  let u = (now - idleAt) % cycle;
  if (u < 0) u += cycle;
  if (u < holdMs) return frozen();
  return { folha: play, now: u - holdMs, once: true };
}

export function arteDa(folha: Folha, dir: number, quadro: number): Quadro | undefined {
  const row = folha.dirs[dir & 7];
  if (!row || row.length === 0) return undefined;
  return row[quadro % row.length];
}

export function desenharFolha(
  ctx: CanvasRenderingContext2D,
  folha: Folha,
  dir: number,
  moving: boolean,
  gait: number,
  now: number,
  rx: number,
  ry: number,
  zoom: number,
  once = false
): void {
  const art = arteDa(folha, dir, quadroDa(folha, moving, gait, now, once));
  if (!art) return;
  desenharQuadro(
    ctx,
    art,
    Math.round(rx - folha.ancoraX * zoom),
    Math.round(ry - folha.ancoraY * zoom),
    folha.cellW * zoom,
    folha.cellH * zoom
  );
}
